import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Eye,
  Shield,
  Scale,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  MousePointer,
  Mail,
  Send,
  Clock,
  BarChart3,
  Database,
  Building2,
  Lock,
  Unlock,
  XCircle,
  Search,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const currentSteps = [
  { num: "1", title: "Consumer signs contract", desc: "For a phone, loan, lease, credit card, or service agreement" },
  { num: "2", title: "Arbitration clause is hidden in fine print", desc: "Buried in pages of legal text most people never read" },
  { num: "3", title: "A dispute occurs", desc: "Billing error, fraud, defective product, unfair treatment" },
  { num: "4", title: "Case goes to private arbitration", desc: "Instead of court — often with an arbitrator chosen by the company" },
  { num: "5", title: "Outcome is not publicly visible", desc: "No public record, no transparency, no precedent" },
  { num: "6", title: "Patterns of abuse are impossible to detect", desc: "Because nobody can see what's happening across thousands of cases" },
];

const policyReforms = [
  {
    id: "4a",
    icon: MousePointer,
    title: "Clear Consumer Choice Requirement",
    desc: "Arbitration clauses must be presented clearly and separately. Consumers must actively choose — no hidden acceptance buried in fine print.",
    highlight: "Like cookie consent online — but for your legal rights.",
  },
  {
    id: "4b",
    icon: Unlock,
    title: "Simple 90-Day Opt-Out System",
    desc: "Every consumer gets a 90-day window to opt out of arbitration after signing. Three easy methods: click a digital button, send an email, or mail a form. No legal language required. No penalties or retaliation.",
    highlight: 'All you write: "I am opting out of arbitration for this account."',
  },
  {
    id: "4c",
    icon: Mail,
    title: "Mandatory Follow-Up Disclosure",
    desc: "Companies must send an email notice AND/OR mailed notice after signing, with a clear explanation of arbitration rights and instructions for opting out.",
    highlight: "Ensures elderly and low-tech users are protected.",
  },
  {
    id: "4d",
    icon: FileText,
    title: "Plain-Language Disclosure Requirement",
    desc: 'Companies must clearly state: arbitration replaces court access, consumers may waive class-action rights, and what arbitration actually means in practice. No misleading marketing like "faster, cheaper, better for everyone."',
    highlight: "Required neutral explanation — not corporate spin.",
  },
  {
    id: "4e",
    icon: BarChart3,
    title: "Outcome Transparency Reporting",
    desc: "Companies must disclose: total arbitration cases, consumer vs. company outcomes, settlement rates, and arbitrator usage patterns. All data reported in a standardized format defined by the State of Nevada.",
    highlight: "Standardized format prevents selective reporting.",
  },
  {
    id: "4f",
    icon: Database,
    title: "Historical Data Disclosure",
    desc: "Companies must disclose 5-10 years of arbitration data where reasonably available — including data maintained by the company or available through arbitration administrators.",
    highlight: "Immediate transparency baseline. No resetting accountability history.",
  },
  {
    id: "4g",
    icon: Building2,
    title: "State Enforcement & Aggregation",
    desc: "Nevada Attorney General can aggregate complaints, identify patterns, and coordinate enforcement with other states. Pattern-based enforcement ensures systemic issues are caught.",
    highlight: "Enforcement-focused — not punitive, but effective.",
  },
];

const faqs = [
  {
    question: "Does this ban arbitration?",
    answer: "No. This plan does not ban arbitration. It requires transparency, clear consent, and public reporting. Companies can still use arbitration — but only with informed consumer choice and full disclosure of outcomes.",
  },
  {
    question: "Can companies still use arbitration?",
    answer: "Yes. Companies can absolutely continue to include arbitration in their agreements. But consumers must clearly understand what they're agreeing to, have a genuine choice, and companies must publicly report outcomes. Fair arbitration has nothing to hide.",
  },
  {
    question: "Why require historical data?",
    answer: "To establish an immediate transparency baseline when the law takes effect. Without historical data, companies could reset their accountability history. By requiring disclosure of existing records, consumers and regulators can see real-world patterns from day one.",
  },
  {
    question: "Will companies punish users for opting out?",
    answer: "No. Retaliation or discrimination against consumers who exercise their opt-out right is prohibited under this framework. You cannot be denied service, charged more, or treated differently for choosing to keep your court access.",
  },
  {
    question: "Does this comply with federal law?",
    answer: "Yes. This plan is specifically designed to comply with the Federal Arbitration Act. It does not ban or invalidate arbitration agreements. It requires transparency, informed consent, and public reporting — all within the legal authority of the state.",
  },
  {
    question: "What if I already signed a contract with arbitration?",
    answer: "This framework applies to new contracts and renewals going forward. However, the historical data disclosure requirement means that past patterns of arbitration outcomes will become publicly visible, giving consumers better information about the companies they do business with.",
  },
];

const ArbitrationPage = () => {
  return (
    <div data-testid="arbitration-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Ending Unfair Arbitration
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            You Should Know What You're Signing Away
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            Nevada families deserve clear, honest contracts — not hidden clauses
            that quietly take away their right to go to court. This plan brings
            transparency, choice, and accountability to arbitration agreements.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            What's Happening Today
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-3xl">
            Right now, most consumers don't even know what they've agreed to.
          </p>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Binding arbitration clauses are hidden in the contracts you sign
                every day — for your phone, your credit card, your lease, your
                loans, your streaming services. Without realizing it, you've
                likely waived your right to go to court dozens of times.
              </p>
              <div className="space-y-4">
                {[
                  {
                    icon: FileText,
                    title: "Hidden in Fine Print",
                    desc: "Arbitration clauses buried in pages of legal text that almost nobody reads",
                  },
                  {
                    icon: Lock,
                    title: "Cases Are Private",
                    desc: "Disputes resolved behind closed doors with no public record",
                  },
                  {
                    icon: XCircle,
                    title: "No Class Actions",
                    desc: "Consumers cannot easily group claims, even when thousands are affected",
                  },
                  {
                    icon: Eye,
                    title: "No Public Data",
                    desc: "Outcomes and fairness data are not publicly available — patterns of abuse stay hidden",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                    >
                      <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold text-red-800 text-sm">{item.title}</p>
                        <p className="text-red-700 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Flow diagram */}
            <div>
              <h3 className="font-bold text-slate-900 mb-4">
                How Arbitration Currently Works
              </h3>
              <div className="space-y-3">
                {currentSteps.map((step) => (
                  <div
                    key={step.num}
                    className="flex items-start gap-3 p-3 bg-[#F8F9FA] border border-slate-200"
                  >
                    <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-slate-800 text-white font-bold text-xs">
                      {step.num}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-sm">
                        {step.title}
                      </p>
                      <p className="text-slate-600 text-xs">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GIVE UP / WHAT THIS RESTORES ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* What you give up */}
            <div className="bg-red-50 border border-red-200 p-8">
              <h3 className="text-lg font-bold text-red-800 mb-6">
                What You Give Up in Arbitration
              </h3>
              <div className="space-y-4">
                {[
                  "Right to a jury trial",
                  "Public visibility of your case",
                  "Class-action participation (in many cases)",
                  "Ability to set legal precedent",
                  "Access to full discovery process",
                ].map((item, i) => (
                  <div key={item} className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-red-800 font-medium text-sm">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* What this plan restores */}
            <div className="bg-emerald-50 border border-emerald-200 p-8">
              <h3 className="text-lg font-bold text-emerald-800 mb-6">
                What This Plan Restores
              </h3>
              <div className="space-y-4">
                {[
                  "Clear, informed consent",
                  "Real choice through 90-day opt-out",
                  "Full transparency of outcomes",
                  "State oversight and enforcement",
                  "Public reporting of arbitration data",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-800 font-medium text-sm">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PLAN ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              A Transparency and Choice Standard for Arbitration
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Seven reforms that bring fairness, transparency, and real choice
              to arbitration agreements in Nevada.
            </p>
          </div>

          <div className="space-y-6">
            {policyReforms.map((reform) => {
              const Icon = reform.icon;
              return (
                <div
                  key={reform.id}
                  className="bg-[#F8F9FA] border border-slate-200 p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-[#002B7F] text-white">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-3">
                        {reform.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed mb-3">
                        {reform.desc}
                      </p>
                      <div className="inline-flex items-center gap-2 bg-[#002B7F]/10 border border-[#002B7F]/20 px-4 py-2">
                        <CheckCircle className="w-4 h-4 text-[#002B7F]" />
                        <span className="text-[#002B7F] font-bold text-sm">
                          {reform.highlight}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── OPT-OUT DETAIL ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
            How the 90-Day Opt-Out Works
          </h2>
          <p className="text-white/70 text-center mb-10">
            Three simple methods — no legal language required, no penalties, no
            retaliation.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: MousePointer,
                title: "Click",
                desc: "Digital button in your account settings — one click, done",
              },
              {
                icon: Send,
                title: "Email",
                desc: 'Send a simple email: "I am opting out of arbitration for this account"',
              },
              {
                icon: Mail,
                title: "Mail",
                desc: "Mail a simple form — accessible for those without internet",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-white/10 border border-white/20 p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-[#D4AF37] text-black mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 bg-white/10 border border-white/20 p-5 text-center">
            <p className="text-[#D4AF37] font-bold">
              Equal access regardless of tech ability. No legal language
              required. No penalties or retaliation.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT THIS MEANS FOR YOU ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">
            What This Means for You
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 text-left">
            {[
              "You understand what you're signing",
              "You get a real choice — not a hidden trap",
              "Companies must be transparent about outcomes",
              "Hidden disputes are no longer invisible",
              "You are no longer isolated in disputes",
              "The state can identify and act on patterns of abuse",
            ].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 bg-white border border-slate-200 p-4"
              >
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-slate-800 font-medium text-sm">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT THIS DOES NOT DO ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Shield className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              What This Does NOT Do
            </h2>
          </div>

          <div className="space-y-4 mb-8">
            {[
              {
                title: "This does NOT ban arbitration",
                desc: "Arbitration remains a lawful option for dispute resolution",
              },
              {
                title: "This does NOT invalidate existing contracts",
                desc: "The framework applies to new contracts and renewals going forward",
              },
              {
                title: "This complies with federal law",
                desc: "Fully consistent with the Federal Arbitration Act — designed for legal durability",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-5 bg-[#F8F9FA] border border-slate-200"
              >
                <Shield className="w-5 h-5 text-[#002B7F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-slate-900">{item.title}</p>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6 text-center">
            <p className="text-[#002B7F] font-bold">
              This plan preserves lawful arbitration agreements while ensuring
              consumers fully understand and meaningfully consent to them.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl font-bold text-slate-900 mb-6">
            This is about restoring balance between everyday people and powerful
            institutions.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-4">
            Because justice should never depend on hidden fine print.
          </p>
          <p className="text-lg text-[#002B7F] font-bold leading-relaxed">
            And accountability should never happen behind closed doors.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Transparency for Nevada Families
          </h2>
          <p className="text-white/70 mb-8">
            Every contract should be honest. Every choice should be real. Every
            outcome should be visible.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Get Involved
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/more-coming-soon"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              More Coming Soon <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArbitrationPage;
